'use client';

import { entityUrl } from '@/lib/entity-url';
import { useRef, useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import { Tag } from '@/types/tags';

interface TagWithCount extends Tag {
    totalCount: number;
    assetImageUrl?: string | null;
    primaryImageUrl?: string;
    assetPrice?: number;
    assetPriceChange24h?: number;
    assetFloorPrice?: number;
    showNewBadge?: boolean;
    hasRecentActivity?: boolean;
}

interface TagsTickerProps {
    tags: TagWithCount[];
    /** "Explore All" link target (defaults to the global /tags directory). */
    exploreHref?: string;
}

export default function TagsTicker({ tags, exploreHref = '/tags' }: TagsTickerProps) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);

    // Check scroll position to toggle arrows
    const checkScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
        }
    };

    // Initialize scroll check on mount
    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => {
            window.removeEventListener('resize', checkScroll);
        };
    }, [tags]);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 400;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth',
            });
        }
    };

    // Mouse wheel horizontal scroll
    const handleWheel = (e: React.WheelEvent) => {
        if (scrollContainerRef.current && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            e.preventDefault();
            scrollContainerRef.current.scrollBy({
                left: e.deltaY,
                behavior: 'smooth',
            });
        }
    };

    return (
        <div className="w-full relative group border-y border-gray-200 dark:border-neutral-900 bg-white dark:bg-brand-dark my-6">
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">

                {/* Left Side: "Explore All" Button */}
                <div className="flex-shrink-0 z-30 bg-white dark:bg-brand-dark border-r border-gray-200 dark:border-neutral-900 h-full">
                    <Link
                        href={exploreHref}
                        className="flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-brand-dark-lighter hover:text-blue-600 dark:hover:text-white transition-colors h-full"
                    >
                        <Layers size={14} />
                        <span className="hidden sm:inline">Explore All</span>
                    </Link>
                </div>

                {/* Scrollable Area Wrapper */}
                <div className="relative flex-1 overflow-hidden flex items-center">
                    {/* Left Arrow */}
                    <div className={`absolute left-0 z-20 h-full flex items-center transition-opacity duration-300 pointer-events-none ${showLeftArrow ? 'opacity-100' : 'opacity-0'}`}>
                        <button
                            onClick={() => scroll('left')}
                            className="pointer-events-auto h-full px-2 bg-gradient-to-r from-white via-white to-transparent dark:from-brand-dark dark:via-brand-dark dark:to-transparent text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-white"
                            aria-label="Scroll left"
                        >
                            <ChevronLeft size={20} />
                        </button>
                    </div>

                    {/* Scroll Container */}
                    <div
                        ref={scrollContainerRef}
                        onScroll={checkScroll}
                        onWheel={handleWheel}
                        className="flex overflow-x-auto scrollbar-hide py-2 px-4 items-center gap-1 scroll-smooth w-full"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {/* Tags */}
                        {tags.map((tag) => {
                            const isToken = tag.linkedAsset?.type === 'token';
                            const isNFT = tag.linkedAsset?.type === 'nft' || tag.linkedAsset?.type === 'collection';

                            // Determine image source (server-side enriched)
                            const showImage = !!(tag.assetImageUrl || tag.primaryImageUrl);
                            const imageUrl = tag.assetImageUrl || tag.primaryImageUrl;

                            return (
                                <Link
                                    key={tag.id}
                                    href={entityUrl(tag.slug, { trailingSlash: false })}
                                    className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-blue-50 dark:hover:bg-brand-dark-lighter transition-colors group/tag"
                                >
                                    {/* NEW Badge */}
                                    {tag.showNewBadge && (
                                        <span className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500 text-white">
                                            NEW
                                        </span>
                                    )}

                                    {/* Recent Activity Indicator */}
                                    {!tag.showNewBadge && tag.hasRecentActivity && (
                                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                    )}

                                    {/* Image: Server-fetched Token, NFT, or Tag Primary Image */}
                                    {showImage && imageUrl && (
                                        <div className={`relative w-5 h-5 overflow-hidden flex-shrink-0 border border-gray-100 dark:border-neutral-700 ${isNFT ? 'rounded' : 'rounded-full'
                                            }`}>
                                            <Image
                                                src={imageUrl}
                                                alt={tag.name}
                                                fill
                                                sizes="20px"
                                                className="object-cover"
                                                loading="lazy"
                                            />
                                        </div>
                                    )}

                                    {/* Tag Name */}
                                    <span className={`text-sm font-medium whitespace-nowrap transition-colors ${showImage
                                        ? 'text-gray-900 dark:text-white'
                                        : 'text-gray-600 dark:text-gray-300 group-hover/tag:text-gray-900 dark:group-hover/tag:text-slate-200'
                                        }`}>
                                        {tag.name}
                                    </span>

                                    {/* Price Display (Tokens/NFTs only, hide on mobile) */}
                                    {(isToken && tag.assetPrice !== undefined) && (
                                        <span className={`hidden md:inline text-[11px] font-semibold whitespace-nowrap ${tag.assetPriceChange24h !== undefined && tag.assetPriceChange24h >= 0
                                            ? 'text-green-500'
                                            : tag.assetPriceChange24h !== undefined
                                                ? 'text-red-500'
                                                : 'text-blue-500'
                                            }`}>
                                            ${tag.assetPrice && tag.assetPrice < 0.01 ? tag.assetPrice.toFixed(6) : tag.assetPrice?.toFixed(2)}
                                            {tag.assetPriceChange24h !== undefined && (
                                                <span className="ml-1">
                                                    {tag.assetPriceChange24h >= 0 ? '+' : ''}
                                                    {tag.assetPriceChange24h.toFixed(1)}%
                                                </span>
                                            )}
                                        </span>
                                    )}

                                    {/* NFT Floor Price */}
                                    {isNFT && tag.assetFloorPrice !== undefined && (
                                        <span className="hidden md:inline text-[11px] font-semibold text-blue-500 whitespace-nowrap">
                                            ⚡ {tag.assetFloorPrice.toFixed(2)} ETH
                                        </span>
                                    )}

                                    {/* Article Count */}
                                    <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-400">
                                        {tag.totalCount}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Right Arrow */}
                    <div className={`absolute right-0 z-20 h-full flex items-center transition-opacity duration-300 pointer-events-none ${showRightArrow ? 'opacity-100' : 'opacity-0'}`}>
                        <button
                            onClick={() => scroll('right')}
                            className="pointer-events-auto h-full px-2 bg-gradient-to-l from-white via-white to-transparent dark:from-brand-dark dark:via-brand-dark dark:to-transparent text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-white"
                            aria-label="Scroll right"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
